import React from 'react'
import styled from 'styled-components'
import {ReportHeaderView} from './viewer/ReportHeaderView'
import {formatDate} from '../components/datepicker'
import {ReportSignatureView} from './viewer/ReportSignatureView'
import {getKeyImageNameOrPlaceholder} from './../lib'


const KEY_IMAGE_PPI = 25.4 / 100
const BROWSER_PPI = 25.4 / 96
const PAPER_HORIZONTAL_MARGINS = 40
const IMAGE_BORDER = 2
const KeyImagePrint = styled.div` {
  ${props => {
  const max = Math.min(((props.paperWidth - IMAGE_BORDER) * BROWSER_PPI) - PAPER_HORIZONTAL_MARGINS, (Math.min(props.width, props.height) * KEY_IMAGE_PPI))
  return `
          width: ${props.width * max / Math.max(props.width, props.height)}mm;
          height: ${props.height * max / Math.max(props.width, props.height)}mm;
          `
}}  
  background-size: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-image: ${props => props.backgroundImage};
  position: relative;
}`

const PageResizerContainer = styled.div`
  overflow: hidden;  
`

const getBackgroundKeyImage = (keyImage, getResource) => keyImage.file ? `url("${URL.createObjectURL(keyImage.file)}")` : `url("${getResource(keyImage.resourceUID)}")`

export class Preview extends React.Component {
  state = {}
  fieldsContainer = null
  headerContainer = null
  compactHeaderContainer = null
  reportContainer = null
  signaturesContainer = null
  footerFiller = null
  lastPageFiller = null
  reportPaginationPageFooter = null
  reportKeyImagesContainer = null

  // resize props
  totalHeight = null
  activeScale = 1

  resize = () => {
    if (!this.reportContainer || this.props.isPdfPrinter) return;
    const resizerContainer = this.reportContainer.parentNode
    const diff = resizerContainer.scrollWidth - resizerContainer.clientWidth
    if (this.activeScale === 1 && diff <= 0) return;
    const {paperSize} = this.props.reportSettings.layout
    const pageWidth = Math.round(paperSize[0] * 96)
    if (this.totalHeight === null) {
      this.totalHeight = parseInt(window.getComputedStyle(resizerContainer).height)
    }
    this.activeScale = Math.min(resizerContainer.clientWidth/pageWidth,1)
    this.reportContainer.style.transform = "scale("+this.activeScale+")";
    this.reportContainer.style.transformOrigin = "left top";
    resizerContainer .style.height = (this.totalHeight * this.activeScale) + "px"
  }

  componentDidMount() {
    window.addEventListener('resize', this.resize)
    this.componentDidUpdate()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize)
  }

  componentWillReceiveProps(newProps) {
    if (newProps.reportSettings.layout.language !== this.props.reportSettings.layout.language)
      this.setState({languageLoaded: false})
  }

  componentDidUpdate() {
    this.props.setImagesLoaded(false)
    if (!this.state.languageLoaded && !this.state.loading) {
      const languageKey = this.props.reportSettings.layout.language
      this.setState({loading: true})
      import(`../components/date.i18n/${languageKey}`).then((lang) => this.setState({
        lang: lang[languageKey],
        languageLoaded: true
      }))
    }
    if (!this.state.languageLoaded) {
      return
    }
    this.reportPaginationPageFooter.className = 'report-page-footer'
    this.compactHeaderContainer.style.display = 'block'
    this.reportFooter.style.display = 'block'
    this.lastPageFiller.style.height = '0'
    this.lastPageFiller.style.display = 'block'
    this.reportKeyImagesContainer.style.display = 'block'
    this.footerFiller.style.height = '0'

    this.clearPagination()

    try {
      this.addPreviewPagination()
    } catch (e) {
      console.log(e)
      this.reportPaginationPageFooter.className = 'report-page-footer-failed'
      this.clearPagination()
    }
    const {lastPageFooter} = this.props.reportSettings.layout
    if (!lastPageFooter)
      this.reportFooter.style.display = 'none'
    this.compactHeaderContainer.style.display = 'none'
    this.reportContainer.style.height = 'auto'
    if (!this.props.isPdfPrinter) {
      this.resize()
    }
  }

  clearPagination() {
    Array.prototype.slice.call(this.reportContainer.querySelectorAll('.page-filler,.page-break,.report-header-fix-container'))
      .forEach(e => e.parentNode.removeChild(e))
    this.reportContainer.normalize()
  }

  addPreviewPagination() {

    this.loadImages()
    const {paperSize, lastPageFooter} = this.props.reportSettings.layout
    this.footerFiller.style.height = 0
    this.reportContainer.style.width = Math.round(paperSize[0] * 96) + 'px'
    this.reportContainer.style.height = (Math.round(paperSize[1] * 96) +
      parseInt(getComputedStyle(this.reportContainer).borderTopWidth, 10) +
      parseInt(getComputedStyle(this.reportContainer).borderBottomWidth, 10)) + 'px'
    const {height: paperHeight, top: containerTopWithBorder} = this.reportContainer.getBoundingClientRect()
    const containerTop = containerTopWithBorder
    const reportContainerStyle = window.getComputedStyle(this.reportContainer)
    const contentMarginTop = (parseFloat(reportContainerStyle.paddingTop, 10) || 0) + (parseFloat(reportContainerStyle.borderTop, 10) || 0)
    const contentMarginBottom = (parseFloat(reportContainerStyle.paddingBottom, 10) || 0) + (parseFloat(reportContainerStyle.borderBottom, 10) || 0)
    const contents = Array.prototype.slice.call(this.fieldsContainer.querySelectorAll('.field-content'))
    const lineHeight = this.getLineHeight(contents)
    const minBreakSize = lineHeight * 3
    const minContentSizeToBreak = lineHeight * 6

    const compactHeaderHeight = this.compactHeaderContainer.offsetHeight

    const contentPageHeight = paperHeight - contentMarginBottom - contentMarginTop - compactHeaderHeight
    const reportFooterHeight = lastPageFooter ? this.reportFooter.offsetHeight : 0
    const contentLastPageHeight = contentPageHeight - reportFooterHeight

    const top = containerTop + contentMarginTop + compactHeaderHeight

    const {top: lastContentsTop, separators} = this.getSeparators(contents, top, {
      contentPageHeight,
      minBreakSize,
      contentLastPageHeight: this.props.reportSignatures.length ? contentPageHeight : contentLastPageHeight,
      canBeLastPage: !this.props.reportSignatures.length,
      minContentSizeToBreak
    }, 0, true)

    const {top: lastSignatureTop, separators: signaturesSeparators} = this.getSeparators(
      Array.prototype.slice.call(this.signaturesContainer.children),
      lastContentsTop,
      {
        contentPageHeight,
        contentLastPageHeight,
        minBreakSize: contentPageHeight,
        canBeLastPage: true,
        minContentSizeToBreak: contentPageHeight
      },
      Preview.getLastDiff(separators), separators.length === 0
    )

    const preFooterSeparators = separators.concat(signaturesSeparators)

    const footerDiff = Math.floor(
      lastSignatureTop + contentPageHeight -
      this.footerFiller.getBoundingClientRect().bottom -
      Preview.getLastDiff(preFooterSeparators)
    )

    const {top: lastKeyImageTop, separators: keyImageSeparators} = this.getSeparators(
      Array.prototype.slice.call(this.reportKeyImagesContainer.children),
      lastSignatureTop,
      {
        contentPageHeight,
        contentLastPageHeight: contentPageHeight,
        minBreakSize: contentPageHeight,
        minContentSizeToBreak: contentPageHeight
      },
      footerDiff, false
    )

    const allSeparators = preFooterSeparators.concat(keyImageSeparators)

    if (this.props.reportKeyImages.length) {
      const lastPageDiff = Math.floor(
        lastKeyImageTop + contentPageHeight -
        this.lastPageFiller.getBoundingClientRect().bottom -
        Preview.getLastDiff(allSeparators)
      )
      this.lastPageFiller.style.height = (lastPageDiff) + 'px'
    } else {
      this.reportKeyImagesContainer.style.display = 'none'
      this.lastPageFiller.style.display = 'none'
    }
    this.footerFiller.style.height = (footerDiff) + 'px'

    allSeparators.reverse().forEach(({diff, position, node}, i) => {
      const page = allSeparators.length - 1 - i
      if (position !== undefined) {
        const range = document.createRange()
        range.setStart(node, position)
        range.setEnd(node, position)
        range.insertNode(this.getPageHeader())
        range.insertNode(this.getPageBreak(page, allSeparators.length))
        range.insertNode(this.getEmptySpace(diff))
      } else {
        node.parentNode.insertBefore(this.getEmptySpace(diff), node)
        node.parentNode.insertBefore(this.getPageBreak(page, allSeparators.length), node)
        node.parentNode.insertBefore(this.getPageHeader(), node)
      }
    })


    if (allSeparators.length === 0) {
      this.reportFooter.style.display = 'none'
    }
  }

  getPageHeader = () => {
    const clone = this.compactHeaderContainer.cloneNode(true)
    const height = this.compactHeaderContainer.offsetHeight
    const container = document.createElement('div')
    container.style.height = height + 'px'
    container.className = 'report-header-fix-container'
    container.appendChild(clone)
    return container
  }

  static getLastDiff(separators) {
    if (!separators.length) return 0
    return separators[separators.length - 1].diff
  }

  getSeparators = (nextNodes, top, opts, lastDiff, firstPage = false) => {
    const {contentPageHeight, minBreakSize, minContentSizeToBreak, contentLastPageHeight} = opts
    let contentBottom = top + contentPageHeight - lastDiff
    let index = nextNodes.findIndex(el => el.getBoundingClientRect().bottom > contentBottom)
    if (index < 0) {
      if (opts.canBeLastPage) {
        const contentLastPageBottom = top + contentLastPageHeight - lastDiff
        const lastPageIndex = nextNodes.findIndex(el => el.getBoundingClientRect().bottom > contentLastPageBottom)
        if (lastPageIndex>=0) {
          index =  lastPageIndex
          contentBottom = contentLastPageBottom
        } else {
          return {top: top, separators: []}
        }
      } else {
        return {top: top, separators: []}
      }
    }
    const nodeToBreak = nextNodes[index]
    let result = {}
    let bottom = 0
    if (nodeToBreak.offsetHeight < minContentSizeToBreak || nodeToBreak.getBoundingClientRect().top + minBreakSize > contentBottom) {
      let parent = null
      if (nodeToBreak.className.indexOf('signature-container') >= 0) {
        parent = nodeToBreak
        bottom = nodeToBreak.getBoundingClientRect().top
      } else if (nodeToBreak.className.indexOf('report-key-image-container') >= 0) {
        parent = nodeToBreak
        bottom = nodeToBreak.getBoundingClientRect().top
      } else {
        parent = nodeToBreak.closest('.report-field-item')
        bottom = nodeToBreak.parentNode.getBoundingClientRect().top
      }
      result = {node: parent}
    } else {
      const {position, node, rect} = this.getTextLine(nodeToBreak, contentBottom)
      if (position !== undefined) {
        bottom = rect.top
        result = {node, position}
      } else {
        bottom = node.getBoundingClientRect().top
        result = {node}
      }
    }
    const diff = contentBottom - bottom
    const {top: lastTop, separators: nextSeparators} = this.getSeparators(nextNodes.slice(index), contentBottom, opts, diff)
    if (nextSeparators.length === 0 && contentLastPageHeight !== contentPageHeight && !firstPage) {
      return this.getSeparators(nextNodes, top, {...opts, contentPageHeight: contentLastPageHeight}, lastDiff)
    }
    return {top: lastTop, separators: [{...result, diff}, ...nextSeparators]}
  }

  getTextLine = (element, bottom) => {
    const textWalker = this.treeWalker(element, NodeFilter.SHOW_ALL)
    while (textWalker.nextNode()) {
      const current = textWalker.currentNode
      if (current.nodeType === Node.TEXT_NODE) {
        const length = current.textContent.length
        const result = this.binarySearchNextLine(current, bottom, 0, length)
        if (result) {
          return result
        }
      } else {
        const rect = current.getBoundingClientRect()
        if (rect.bottom > bottom) {
          return {node: current}
        }
      }
    }
    return null
  }

  binarySearchNextLine = (node, bottomBoundary, begin, textWindow) => {
    const max = node.textContent.length
    const end = Math.max(0, Math.min(begin + textWindow, max))
    const realWindow = end - begin
    const rect = this.getTextRect(node, end)
    if (realWindow <= 1) {
      if (end <= 1 && node.parentNode.nodeName.toUpperCase() === 'LI') { // LI fix
        if (rect.bottom < bottomBoundary)
          return null
        const newNode = document.createElement('span')
        if (node.parentNode.previousSibling) {
          node.parentNode.previousSibling.appendChild(newNode)
          return {node: newNode, rect: rect, position: 0}
        } else if (node.parentNode.parentNode.className.indexOf('field-content' >= 0)) {
          return {node: node.parentNode.parentNode, rect: node.parentNode.parentNode.getBoundingClientRect()}
        } else {
          node.parentNode.parentNode.insertBefore(node.parentNode, newNode)
          return {node: newNode, react: rect, position: 0}
        }
      }

      const range = document.createRange()
      if (end > 0) {// firefox fix
        range.setStart(node, end - 1)
        range.setEnd(node, Math.min(end + 1, node.textContent.length))
      }
      const nonCollapsedRect = range.getBoundingClientRect()
      const endTextRect = this.getTextRect(node, end - 1)
      if (end > 0 && (endTextRect.bottom >= bottomBoundary ||
        (nonCollapsedRect.left < rect.left && nonCollapsedRect.bottom >= bottomBoundary))) // firefox fix
      {
        return {
          position: end - 1,
          node: node,
          rect: this.isRectEmpty(nonCollapsedRect) ? endTextRect : nonCollapsedRect
        }
      } else if (rect.bottom >= bottomBoundary) {
        return {position: end, node: node, rect}
      } else if (node.textContent.length > end) {
        return {position: end + 1, node: node, rect: this.getTextRect(node, end + 1)}
      } else {
        return null
      }
    } else {
      if (rect.bottom <= bottomBoundary) {
        if (textWindow === realWindow) {
          return this.binarySearchNextLine(node, bottomBoundary, begin + textWindow, Math.ceil(textWindow / 2))
        } else {
          return null
        }
      } else {
        return this.binarySearchNextLine(node, bottomBoundary, begin, Math.ceil(textWindow / 2))
      }
    }
  }

  isRectEmpty = (rect) => rect.top === 0 && rect.bottom === 0 && rect.left === 0 && rect.right === 0

  getTextRect = (node, pos = 0) => {
    const range = document.createRange()
    range.setStart(node, pos)
    range.setEnd(node, pos)
    const rect = range.getBoundingClientRect()
    if (this.isRectEmpty(rect)) { // safari fix
      const span = document.createElement('span')
      span.innerHTML = '&#8203;'
      if (pos === 0) {
        node.parentNode.insertBefore(span, node)
      } else {
        range.insertNode(span)
      }
      const spanRect = span.getBoundingClientRect()
      const parent = span.parentNode
      parent.removeChild(span)
      parent.normalize()
      return spanRect
    }
    return rect
  }

  getEmptySpace = (size) => {
    const div = document.createElement('div')
    div.className = 'page-filler'
    div.style.height = size + 'px'
    return div
  }

  getPageBreak = (page, totalPages) => {
    const div = document.createElement('div')
    const span = document.createElement('span')
    span.innerHTML = 'Page ' + (page + 1) + ' / ' + (totalPages + 1)
    span.className = 'page-counter'
    div.appendChild(span)
    div.className = 'page-break'
    return div
  }

  getLineHeight = (contents) => {
    const content = Array.prototype.slice.call(contents).find(el => el.textContent)
    if (!content)
      return 0
    const textWalker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null, false)
    const text = textWalker.nextNode()
    return this.getTextRect(text).height
  }

  treeWalker = (element) =>
    document.createTreeWalker(
      element,
      NodeFilter.SHOW_ALL,
      (e) => {
        if (e.nodeType === Node.TEXT_NODE) {
          return NodeFilter.FILTER_ACCEPT
        } else {
          return e.childNodes.length ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_ACCEPT
        }
      },
      false
    )

  loadImages = () => {
    const logo = this.props.reportSettings.organization.logo
    const imagesToLoad = this.props.reportSignatures.map(({signatureId}) => this.props.availableSignatures[signatureId]).filter(_ => _).map(_ => _.digitalSignature)
      .concat([logo]).filter(d => !!d)
      .concat(this.props.reportKeyImages.map(_ => _.resourceUID))
      .map(imageSrc =>
        new Promise((resolve, reject) => {
          const img = document.createElement('img')
          img.onload = resolve
          img.onerror = resolve
          img.src = this.props.getResource(imageSrc)
        })
      )
    Promise.all(imagesToLoad).then(loaded => this.props.setImagesLoaded(true))
  }

  render() {
    const {finished, Fill, lastSaved, reportSettings: {organization, layout}, getResource, reportKeyImages, patientName, reportTitle, reportBasicInfos, reportFields, reportSignatures, availableSignatures} = this.props
    const {lang, languageLoaded} = this.state
    if (!languageLoaded) {
      return null
    }
    return (
      <PageResizerContainer>
      <div ref={el => this.reportContainer = el} className="report-page">

        {reportKeyImages.filter(_ => _.uploadStatus).length > 0 &&
        <Fill name="Notifications">
          <div className="system-notification with-loader light">
            <span>There are key images still uploading...</span>
          </div>
        </Fill>
        }
        <div ref={el => this.headerContainer = el} className={'report-header ' + layout.headerLayout}>
          <ReportHeaderView organization={organization} headerLayout={layout.headerLayout} getResource={getResource}/>
        </div>

        <div className="report-title-container">
          <h2 className="report-title">{reportTitle.title}</h2>
          <div className="report-date-container">
            {
              lang &&
              <span className="report-date">{
                finished ?
                  formatDate(new Date(lastSaved), lang, layout.dateFormat + ' - hh:mm') :
                  formatDate(new Date(lastSaved), lang, layout.dateFormat)
              }</span>
            }
          </div>
        </div>


        {
          !!reportBasicInfos.length &&
          <div className="report-basic-info">
            <div className="box report-basic-info-box">
              <div className="column-wrapper">
                {reportBasicInfos.filter((_, i) => (i + 1) % 2).map(info =>
                  <div className="report-basic-info-item" key={info.id}>
                    <label>{info.label}:</label> <span>{info.value}</span>
                  </div>
                )}
              </div>
              <div className="column-wrapper">
                {reportBasicInfos.filter((_, i) => i % 2).map(info =>
                  <div className="report-basic-info-item" key={info.id}>
                    <label>{info.label}:</label> <span>{info.value}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        }
        <div ref={el => this.fieldsContainer = el} className="report-fields-list">
          {reportFields.map(field =>
            <div className="report-field-item" key={field.id}>
              <label>{field.label}:</label>
              <div className="field-content" dangerouslySetInnerHTML={{__html: field.value}}/>
            </div>)}
        </div>

        <div className="report-signatures" ref={el => this.signaturesContainer = el}>
          {reportSignatures.filter(_ => availableSignatures[_.signatureId]).reduce(groupByTwo, []).map(responsibles =>
            <div key={responsibles[0].id} className="drag-drop-limits double-signature-container">
              {responsibles.map(responsible =>
                <div key={responsible.id} className={'signature-container ' + responsible.structure}>
                  <ReportSignatureView
                    responsible={responsible}
                    getResource={this.props.getResource}
                    signature={availableSignatures[responsible.signatureId]}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="report-compact-header" ref={el => this.compactHeaderContainer = el}>
          <div className="report-compact-header-content">
            <span>{organization.organizationName}</span>
            <span>Patient: {patientName}</span>
          </div>
          <div className="report-compact-header-margin"/>
        </div>
        <div className="footer-filler" ref={el => this.footerFiller = el}>
          <div className="report-footer" ref={el => this.reportFooter = el}>
            <div className="organization-details">
              {organization.organizationName.trim() &&
              <span className="organization-name">{organization.organizationName}</span>}
              <div className="address-details">
                {organization.address.trim() && <span className="address">{organization.address} </span>}
                {organization.city.trim() && <span className="city">{organization.city}</span>}
                {organization.state.trim() && <span className="state"> {organization.state}</span>}
                {organization.zipCode.trim() && <span className="zipcode">{organization.zipCode}</span>}
                {organization.country.trim() && <span className="country">{organization.country} </span>}
              </div>
              {organization.phoneNumber.trim() && <span className="phone">Phone: {organization.phoneNumber} </span>}
              {organization.faxNumber.trim() && <span className="fax">Fax: {organization.faxNumber} </span>}
              {organization.email.trim() && <span className="email">Email: {organization.email} </span>}
            </div>
          </div>

        </div>
        <div className="report-key-images-container" ref={el => this.reportKeyImagesContainer = el}>
          {reportKeyImages.map((keyImage, i) =>
            <div className="report-key-image-container" key={keyImage.id}>
              <div className="image-container">
                <KeyImagePrint
                  height={keyImage.imageHeight}
                  width={keyImage.imageWidth}
                  paperWidth={(this.props.reportSettings.layout.paperSize[0]) * 96}
                  backgroundImage={getBackgroundKeyImage(keyImage, this.props.getResource)}
                />
              </div>
              <div className="text-container">
                <div className="report-key-image-label">{getKeyImageNameOrPlaceholder(keyImage, i)}</div>
                {keyImage.description.trim() &&
                <div className="report-key-image-description">{keyImage.description}</div>}
              </div>
            </div>
          )}
        </div>
        <div className="report-page-footer" ref={el => this.reportPaginationPageFooter = el}/>
        <div className="last-page-filler" ref={el => this.lastPageFiller = el}/>
      </div>
      </PageResizerContainer>
    )
  }
}

function groupByTwo(acc, item, i) {
  return [
    ...acc.slice(0, i / 2),
    (i % 2 ?
        [...acc[Math.floor(i / 2)], item] :
        [item]
    )
  ]
}



// WEBPACK FOOTER //
// ./~/report-components/src/modules/Preview.jsx